import React from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {BrandBuildingCubeIcon} from "@pg-design/icons-module";
import {useElementWasVisible, useIsMounted} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {clientRpRequestMeta} from "../../../client/client_rp_request_meta";
import {ApplicationBox} from "../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {ArchitectOfferPromoBox} from "../../architect_offers/components/ArchitectOfferPromoBox";
import {useMarketplaceArchitects} from "../../architect_offers/hooks/use_marketplace_architects";
import {useMarketplaceRegions} from "../../architect_offers/hooks/use_marketplace_regions";
import {ContactBar} from "../../atoms/contact_bar/ContactBar";
import {DetailConstructionGallerySection} from "../../atoms/DetailConstructionGallerySection";
import {TimeStamp} from "../../atoms/TimeStamp";
import {TwoColumnGridAside, TwoColumnGridHolder, TwoColumnGridMain} from "../../atoms/TwoColumnGrid";
import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {LeadSidebar} from "../../lead/components/lead_sidebar/LeadSidebar";
import {SidebarTestContainer} from "../../lead/components/lead_sidebar/SidebarTestContainer";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {useGetOfferListQuery} from "../../offer/api/get_offer_list";
import {OfferSalesOffices} from "../../offer/atoms/OfferSalesOffices";
import {DetailNearbyOffers} from "../../offer/components/DetailNearbyOffers";
import {OfferDetailOpinions} from "../../offer/components/opinions/OfferDetailOpinions";
import {useYslabPlannerPropertyData} from "../../offer/detail/planner3D/components/hooks/use_yslab_planner_property_data";
import {Planner3dModal} from "../../offer/detail/planner3D/components/Planner3D";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {OfferType} from "../../offer/types/OfferType";
import {getDetailPageNearbyOffersPayload} from "../../offer/utils/get_detail_page_nearby_offers_payload";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {Promotions} from "../../promotion/components/Promotions";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {VendorTrustCertificate} from "../../vendor/components/VendorTrustCertificate";
import {ViewType} from "../../view_type/ViewType";
import {fetchOtherPropertiesInOfferView} from "../actions/fetch_other_properties_in_offer";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IOtherPropertiesInOfferStore} from "../reducers/other_properties_in_offer_reducer";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {PropertyCreditCalculator} from "./property_credit_calculator/PropertyCreditCalculator";
import {PropertyDetailDescription} from "./PropertyDetailDescription";
import {PropertyDetailDetails} from "./PropertyDetailDetails";
import {PropertyDetailLocation} from "./PropertyDetailLocation";
import {PropertyDetailOtherPropertiesInOffer} from "./PropertyDetailOtherPropertiesInOffer";
import {PropertyNavBar} from "./PropertyNavBar";

export const OSM_MAP_CONTAINER = "osm-map-container";

const PropertyEstimatedPrices = React.lazy(() => import("./PropertyEstimatedPrices"));

interface IProps {
    offer: IPropertyDetailOffer;
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[];
    variables: IVariables | null;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    otherPropertiesInOffer: IOtherPropertiesInOfferStore;
    showPriceAndContactButtons: boolean;
    shouldMapBeRendered?: boolean;
}

const PROPERTY_DETAIL_LOCATION_CONTAINER = "property-detail-location-container";

export const PropertyDetailForSale: React.FC<IProps> = (props) => {
    const {promotions, property, offer, variables} = props;

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {regionBySlug} = useMarketplaceRegions(offer.region.stats?.region_type_city?.slug);
    const architects = useMarketplaceArchitects({
        withRegionOnly: true,
        regionId: regionBySlug?.id
    });
    const {data: nearbyOffersResponse} = useGetOfferListQuery(getDetailPageNearbyOffersPayload(offer));

    const hasPromotions = promotions.length > 0;
    const nearbyOffers = nearbyOffersResponse ? nearbyOffersResponse.results : [];
    const hasOffersNearby = nearbyOffers.length > 0;
    const isAbroad = offer.region.country !== Country.POLAND;

    const contactBarProps = {
        trackOffer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        trackVendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        trackProperty: {
            area: property.area,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };
    const showVendorTrustCertificate = !!offer.vendor.awards_count;
    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const hasPropertyShownPrice = shouldShowPrice && Boolean(property.price);

    const {yslabProperty} = useYslabPlannerPropertyData({offerId: offer.id, property});

    const dispatch = useDispatch();

    useElementWasVisible({
        ids: [PROPERTY_DETAIL_LOCATION_CONTAINER],
        onElementVisible: () => {
            const ctx = {
                meta: clientRpRequestMeta,
                offerId: offer.id,
                propertyId: property.id,
                rooms: property.rooms
            };
            dispatch(fetchOtherPropertiesInOfferView(ctx));
        },
        once: true,
        threshold: 0.05
    });

    const applicationBoxProps = {
        storeHash: "offerDetailSide",
        source: property.is_reserved ? ApplicationSource.Reservation : ApplicationSource.Property,
        sourceSection: ApplicationSourceSection.SIDEBAR,
        vendorId: offer.vendor.id,
        offerId: offer.id,
        property: property,
        offerStatusStatus: offer?.status?.status,
        formButtonText: hasPropertyShownPrice ? "Zapytaj dewelopera" : undefined,
        optionalOfferPhoneData: offer,
        optionalVendorInfoData: offer.vendor,
        isAutoresponderEnabled: offer.configuration.is_autoresponder_enabled,
        forceClose: getOfferDisplayStatus(property.offer) === "limited"
    };

    return (
        <>
            <PropertyNavBar
                className={navBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                offer={offer}
                property={property}
                onButtonClick={props.changeModalTab}
            />

            <TwoColumnGridHolder>
                <TwoColumnGridMain className={propertyDetailForSaleHolder}>
                    {hasPromotions && (
                        <section id="promocje" className={css({mb: 3})}>
                            <Promotions
                                promotions={promotions}
                                vendorId={offer.vendor.id}
                                offerId={offer.id}
                                propertyId={property.id}
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                isSaleOverLimit={getOfferDisplayStatus(offer) === "limited"}
                            />
                        </section>
                    )}
                    <section className={css({mb: 5})}>
                        <PropertyDetailDetails property={property} variables={variables} />
                    </section>

                    <section className={detailConstructionSectionStyle}>
                        <DetailConstructionGallerySection
                            onButtonClick={() => props.changeModalTab(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BANNER)}
                            type="property"
                        />
                    </section>
                    <section className={css({mb: 2})} data-id={PROPERTY_DETAIL_LOCATION_CONTAINER}>
                        {props.shouldMapBeRendered && (
                            <PropertyDetailLocation
                                changeModalTab={props.changeModalTab}
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                            />
                        )}
                    </section>
                    <section className={css({mb: 2})}>
                        <OfferDetailOpinions offer={offer} property={property} />
                    </section>
                    {/*ArchitectOffersBox anchor*/}
                    <a id="modul_wykonczenia" />
                    {offer.region.stats.region_type_city?.slug &&
                    regionBySlug?.id &&
                    architects.length > 0 &&
                    (offer.type === OfferType.FLAT || offer.type === OfferType.HOUSE) ? (
                        <section className={css({my: 5})}>
                            <ArchitectOfferPromoBox
                                offerType={offer.type}
                                regionSlug={offer.region.stats.region_type_city?.slug}
                                minPriceM2={architects[0].min_price_m2}
                            />
                        </section>
                    ) : null}
                    {props.otherPropertiesInOffer.count > 0 && (
                        <section className={css({mb: 3})}>
                            <PropertyDetailOtherPropertiesInOffer
                                mainProperty={props.property}
                                properties={props.otherPropertiesInOffer.list}
                                count={props.otherPropertiesInOffer.count}
                                offerId={props.offer.id}
                                propertyId={props.property.id}
                                availableRooms={props.offer.stats?.rooms ?? []}
                                rooms={props.property.rooms}
                            />
                        </section>
                    )}
                    <section className={plannerButtonSectionStyle}>
                        <Planner3dModal
                            offer={props.offer}
                            vendorId={props.offer.vendor.id}
                            routedModalId="property"
                            yslabProperty={yslabProperty}
                            initialFloor={property.floor}
                            initialBuildingUuid={property.building_uuid}
                        >
                            <Button className={brandButtonStyle} iconLeft={BrandBuildingCubeIcon} variant="brand_icon" size={isMobile ? "medium" : "big"}>
                                Plan pięter
                            </Button>
                        </Planner3dModal>
                    </section>
                    <section className={css({mb: 2})}>
                        <PropertyDetailDescription property={property} variables={variables} />
                    </section>
                    {showVendorTrustCertificate && (
                        <section className={css({mb: 2})}>
                            <VendorTrustCertificate vendor={offer.vendor} />
                        </section>
                    )}
                    <section className={css({mb: 2})}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} showBorders />
                    </section>
                    {/*trigger for chatbot hotline modal*/}
                    <div id={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} />
                    {shouldShowPrice && offer.type !== OfferType.COMMERCIAL && offer.region.country === Country.POLAND && (
                        <section>
                            <PropertyCreditCalculator property={property} currency={offer.currency} />
                        </section>
                    )}
                    {isMounted && !props.property.price && !props.property.price_m2 && props.showPriceAndContactButtons && (
                        <section className={css({mb: 2})}>
                            <React.Suspense fallback={<React.Fragment />}>
                                <PropertyEstimatedPrices offerType={offer.type} region={offer.region.full_name} property={property} />
                            </React.Suspense>
                        </section>
                    )}
                    <section className={css({mb: 2})}>
                        <OfferSalesOffices offer={offer} />
                    </section>
                </TwoColumnGridMain>

                <TwoColumnGridAside>
                    <div className={extendedApplicationSidebarWrapper}>
                        {props.showPriceAndContactButtons && !isAbroad ? (
                            <SidebarTestContainer>
                                {[
                                    () => <ApplicationBox {...applicationBoxProps} officeData={offer.sales_offices[0]} />,
                                    () => (
                                        <LeadSidebar
                                            vendor={offer.vendor}
                                            offerId={offer.id}
                                            propertyId={property.id}
                                            source={applicationBoxProps.source}
                                            sourceSection={applicationBoxProps.sourceSection}
                                        />
                                    )
                                ]}
                            </SidebarTestContainer>
                        ) : (
                            <ApplicationBox {...applicationBoxProps} officeData={offer.sales_offices[0]} />
                        )}
                    </div>
                </TwoColumnGridAside>
            </TwoColumnGridHolder>

            {hasOffersNearby && (
                <WideContent className={offersNearbyWrapper}>
                    <DetailNearbyOffers viewType={ViewType.PROPERTY} offer={offer} />
                </WideContent>
            )}

            {props.showPriceAndContactButtons && (
                <ContactBar
                    salesOffices={offer.sales_offices}
                    vendor={offer.vendor}
                    trackVendor={contactBarProps.trackVendor}
                    trackOffer={contactBarProps.trackOffer}
                    trackProperty={contactBarProps.trackProperty}
                    propertyId={property.id}
                    source={ApplicationSource.Property}
                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                    offer={offer}
                    propertyPrice={property.price}
                />
            )}
        </>
    );
};

const propertyDetailForSaleHolder = css({
    md: {
        overflow: "hidden",
        marginTop: "-10rem" // use negative margin to push section to mid level of nav bar. Nav is element that hides and shows with sticky position depending on width. Negative margin is safe and easy way to solve layout issue
    }
});

const detailConstructionSectionStyle = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const navBarWrapperStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        paddingTop: "60px" // other bars height
    }
});

const navBarContentStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        borderTop: "1px solid var(--colors-gray-400)"
    }
});

const offersNearbyWrapper = css({
    mt: 6,
    mb: 10
});

const extendedApplicationSidebarWrapper = css({
    position: "sticky",
    padding: 0,
    paddingBottom: 5,
    top: "120px",

    "@media (min-width: 768px) and (max-width: 1310px)": {
        top: "180px" // 120px + 60px (nav bar height)
    }
});

const plannerButtonSectionStyle = css({
    mb: 2,
    borderRadius: 2,
    backgroundColor: "#fff",
    textAlign: "center"
});

const brandButtonStyle = css({
    padding: 3,
    width: "100%",
    height: "100%"
});
import React from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {BrandBuildingCubeIcon} from "@pg-design/icons-module";
import {useElementWasVisible, useIsMounted} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {clientRpRequestMeta} from "../../../client/client_rp_request_meta";
import {ApplicationBox} from "../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {ArchitectOfferPromoBox} from "../../architect_offers/components/ArchitectOfferPromoBox";
import {useMarketplaceArchitects} from "../../architect_offers/hooks/use_marketplace_architects";
import {useMarketplaceRegions} from "../../architect_offers/hooks/use_marketplace_regions";
import {ContactBar} from "../../atoms/contact_bar/ContactBar";
import {DetailConstructionGallerySection} from "../../atoms/DetailConstructionGallerySection";
import {TimeStamp} from "../../atoms/TimeStamp";
import {TwoColumnGridAside, TwoColumnGridHolder, TwoColumnGridMain} from "../../atoms/TwoColumnGrid";
import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {LeadSidebar} from "../../lead/components/lead_sidebar/LeadSidebar";
import {SidebarTestContainer} from "../../lead/components/lead_sidebar/SidebarTestContainer";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {useGetOfferListQuery} from "../../offer/api/get_offer_list";
import {OfferSalesOffices} from "../../offer/atoms/OfferSalesOffices";
import {DetailNearbyOffers} from "../../offer/components/DetailNearbyOffers";
import {OfferDetailOpinions} from "../../offer/components/opinions/OfferDetailOpinions";
import {useYslabPlannerPropertyData} from "../../offer/detail/planner3D/components/hooks/use_yslab_planner_property_data";
import {Planner3dModal} from "../../offer/detail/planner3D/components/Planner3D";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {OfferType} from "../../offer/types/OfferType";
import {getDetailPageNearbyOffersPayload} from "../../offer/utils/get_detail_page_nearby_offers_payload";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {Promotions} from "../../promotion/components/Promotions";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {VendorTrustCertificate} from "../../vendor/components/VendorTrustCertificate";
import {ViewType} from "../../view_type/ViewType";
import {fetchOtherPropertiesInOfferView} from "../actions/fetch_other_properties_in_offer";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IOtherPropertiesInOfferStore} from "../reducers/other_properties_in_offer_reducer";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {PropertyCreditCalculator} from "./property_credit_calculator/PropertyCreditCalculator";
import {PropertyDetailDescription} from "./PropertyDetailDescription";
import {PropertyDetailDetails} from "./PropertyDetailDetails";
import {PropertyDetailLocation} from "./PropertyDetailLocation";
import {PropertyDetailOtherPropertiesInOffer} from "./PropertyDetailOtherPropertiesInOffer";
import {PropertyNavBar} from "./PropertyNavBar";

export const OSM_MAP_CONTAINER = "osm-map-container";

const PropertyEstimatedPrices = React.lazy(() => import("./PropertyEstimatedPrices"));

interface IProps {
    offer: IPropertyDetailOffer;
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[];
    variables: IVariables | null;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    otherPropertiesInOffer: IOtherPropertiesInOfferStore;
    showPriceAndContactButtons: boolean;
    shouldMapBeRendered?: boolean;
}

const PROPERTY_DETAIL_LOCATION_CONTAINER = "property-detail-location-container";

export const PropertyDetailForSale: React.FC<IProps> = (props) => {
    const {promotions, property, offer, variables} = props;

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {regionBySlug} = useMarketplaceRegions(offer.region.stats?.region_type_city?.slug);
    const architects = useMarketplaceArchitects({
        withRegionOnly: true,
        regionId: regionBySlug?.id
    });
    const {data: nearbyOffersResponse} = useGetOfferListQuery(getDetailPageNearbyOffersPayload(offer));

    const hasPromotions = promotions.length > 0;
    const nearbyOffers = nearbyOffersResponse ? nearbyOffersResponse.results : [];
    const hasOffersNearby = nearbyOffers.length > 0;
    const isAbroad = offer.region.country !== Country.POLAND;

    const contactBarProps = {
        trackOffer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        trackVendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        trackProperty: {
            area: property.area,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };
    const showVendorTrustCertificate = !!offer.vendor.awards_count;
    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const hasPropertyShownPrice = shouldShowPrice && Boolean(property.price);

    const {yslabProperty} = useYslabPlannerPropertyData({offerId: offer.id, property});

    const dispatch = useDispatch();

    useElementWasVisible({
        ids: [PROPERTY_DETAIL_LOCATION_CONTAINER],
        onElementVisible: () => {
            const ctx = {
                meta: clientRpRequestMeta,
                offerId: offer.id,
                propertyId: property.id,
                rooms: property.rooms
            };
            dispatch(fetchOtherPropertiesInOfferView(ctx));
        },
        once: true,
        threshold: 0.05
    });

    const applicationBoxProps = {
        storeHash: "offerDetailSide",
        source: property.is_reserved ? ApplicationSource.Reservation : ApplicationSource.Property,
        sourceSection: ApplicationSourceSection.SIDEBAR,
        vendorId: offer.vendor.id,
        offerId: offer.id,
        property: property,
        offerStatusStatus: offer?.status?.status,
        formButtonText: hasPropertyShownPrice ? "Zapytaj dewelopera" : undefined,
        optionalOfferPhoneData: offer,
        optionalVendorInfoData: offer.vendor,
        isAutoresponderEnabled: offer.configuration.is_autoresponder_enabled,
        forceClose: getOfferDisplayStatus(property.offer) === "limited"
    };

    return (
        <>
            <PropertyNavBar
                className={navBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                offer={offer}
                property={property}
                onButtonClick={props.changeModalTab}
            />

            <TwoColumnGridHolder>
                <TwoColumnGridMain className={propertyDetailForSaleHolder}>
                    {hasPromotions && (
                        <section id="promocje" className={css({mb: 3})}>
                            <Promotions
                                promotions={promotions}
                                vendorId={offer.vendor.id}
                                offerId={offer.id}
                                propertyId={property.id}
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                isSaleOverLimit={getOfferDisplayStatus(offer) === "limited"}
                            />
                        </section>
                    )}
                    <section className={css({mb: 5})}>
                        <PropertyDetailDetails property={property} variables={variables} />
                    </section>

                    <section className={detailConstructionSectionStyle}>
                        <DetailConstructionGallerySection
                            onButtonClick={() => props.changeModalTab(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BANNER)}
                            type="property"
                        />
                    </section>
                    <section className={css({mb: 2})} data-id={PROPERTY_DETAIL_LOCATION_CONTAINER}>
                        {props.shouldMapBeRendered && (
                            <PropertyDetailLocation
                                changeModalTab={props.changeModalTab}
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                            />
                        )}
                    </section>
                    <section className={css({mb: 2})}>
                        <OfferDetailOpinions offer={offer} property={property} />
                    </section>
                    {/*ArchitectOffersBox anchor*/}
                    <a id="modul_wykonczenia" />
                    {offer.region.stats.region_type_city?.slug &&
                    regionBySlug?.id &&
                    architects.length > 0 &&
                    (offer.type === OfferType.FLAT || offer.type === OfferType.HOUSE) ? (
                        <section className={css({my: 5})}>
                            <ArchitectOfferPromoBox
                                offerType={offer.type}
                                regionSlug={offer.region.stats.region_type_city?.slug}
                                minPriceM2={architects[0].min_price_m2}
                            />
                        </section>
                    ) : null}
                    {props.otherPropertiesInOffer.count > 0 && (
                        <section className={css({mb: 3})}>
                            <PropertyDetailOtherPropertiesInOffer
                                mainProperty={props.property}
                                properties={props.otherPropertiesInOffer.list}
                                count={props.otherPropertiesInOffer.count}
                                offerId={props.offer.id}
                                propertyId={props.property.id}
                                availableRooms={props.offer.stats?.rooms ?? []}
                                rooms={props.property.rooms}
                            />
                        </section>
                    )}
                    <section className={plannerButtonSectionStyle}>
                        <Planner3dModal
                            offer={props.offer}
                            vendorId={props.offer.vendor.id}
                            routedModalId="property"
                            yslabProperty={yslabProperty}
                            initialFloor={property.floor}
                            initialBuildingUuid={property.building_uuid}
                        >
                            <Button className={brandButtonStyle} iconLeft={BrandBuildingCubeIcon} variant="brand_icon" size={isMobile ? "medium" : "big"}>
                                Plan pięter
                            </Button>
                        </Planner3dModal>
                    </section>
                    <section className={css({mb: 2})}>
                        <PropertyDetailDescription property={property} variables={variables} />
                    </section>
                    {showVendorTrustCertificate && (
                        <section className={css({mb: 2})}>
                            <VendorTrustCertificate vendor={offer.vendor} />
                        </section>
                    )}
                    <section className={css({mb: 2})}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} showBorders />
                    </section>
                    {/*trigger for chatbot hotline modal*/}
                    <div id={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} />
                    {shouldShowPrice && offer.type !== OfferType.COMMERCIAL && offer.region.country === Country.POLAND && (
                        <section>
                            <PropertyCreditCalculator property={property} currency={offer.currency} />
                        </section>
                    )}
                    {isMounted && !props.property.price && !props.property.price_m2 && props.showPriceAndContactButtons && (
                        <section className={css({mb: 2})}>
                            <React.Suspense fallback={<React.Fragment />}>
                                <PropertyEstimatedPrices offerType={offer.type} region={offer.region.full_name} property={property} />
                            </React.Suspense>
                        </section>
                    )}
                    <section className={css({mb: 2})}>
                        <OfferSalesOffices offer={offer} />
                    </section>
                </TwoColumnGridMain>

                <TwoColumnGridAside>
                    <div className={extendedApplicationSidebarWrapper}>
                        {props.showPriceAndContactButtons && !isAbroad ? (
                            <SidebarTestContainer>
                                {[
                                    () => <ApplicationBox {...applicationBoxProps} officeData={offer.sales_offices[0]} />,
                                    () => (
                                        <LeadSidebar
                                            vendor={offer.vendor}
                                            offerId={offer.id}
                                            propertyId={property.id}
                                            source={applicationBoxProps.source}
                                            sourceSection={applicationBoxProps.sourceSection}
                                        />
                                    )
                                ]}
                            </SidebarTestContainer>
                        ) : (
                            <ApplicationBox {...applicationBoxProps} officeData={offer.sales_offices[0]} />
                        )}
                    </div>
                </TwoColumnGridAside>
            </TwoColumnGridHolder>

            {hasOffersNearby && (
                <WideContent className={offersNearbyWrapper}>
                    <DetailNearbyOffers viewType={ViewType.PROPERTY} offer={offer} />
                </WideContent>
            )}

            {props.showPriceAndContactButtons && (
                <ContactBar
                    salesOffices={offer.sales_offices}
                    vendor={offer.vendor}
                    trackVendor={contactBarProps.trackVendor}
                    trackOffer={contactBarProps.trackOffer}
                    trackProperty={contactBarProps.trackProperty}
                    propertyId={property.id}
                    source={ApplicationSource.Property}
                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                    offer={offer}
                    propertyPrice={property.price}
                />
            )}
        </>
    );
};

const propertyDetailForSaleHolder = css({
    md: {
        overflow: "hidden",
        marginTop: "-10rem" // use negative margin to push section to mid level of nav bar. Nav is element that hides and shows with sticky position depending on width. Negative margin is safe and easy way to solve layout issue
    }
});

const detailConstructionSectionStyle = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const navBarWrapperStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        paddingTop: "60px" // other bars height
    }
});

const navBarContentStyle = css({
    "@media (min-width: 768px) and (max-width: 1310px)": {
        borderTop: "1px solid var(--colors-gray-400)"
    }
});

const offersNearbyWrapper = css({
    mt: 6,
    mb: 10
});

const extendedApplicationSidebarWrapper = css({
    position: "sticky",
    padding: 0,
    paddingBottom: 5,
    top: "120px",

    "@media (min-width: 768px) and (max-width: 1310px)": {
        top: "180px" // 120px + 60px (nav bar height)
    }
});

const plannerButtonSectionStyle = css({
    mb: 2,
    borderRadius: 2,
    backgroundColor: "#fff",
    textAlign: "center"
});

const brandButtonStyle = css({
    padding: 3,
    width: "100%",
    height: "100%"
});
